import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, IndianRupee, Calendar, FileText, Download, Loader2, AlertTriangle, CheckCircle, Receipt, Bug, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnerBilling = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [billingData, setBillingData] = useState({
    invoices: [],
    onboarding_paid: false,
    total_paid: 0
  });
  const [onboardingStatus, setOnboardingStatus] = useState({
    onboarding_paid: true,  // Default to true until API returns actual status
    onboarding_fee: 1999
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isPayingOnboarding, setIsPayingOnboarding] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [debuggingInvoice, setDebuggingInvoice] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('partnerToken');
    return { Authorization: `Bearer ${token}` };
  };

  // Handle payment success redirect from Razorpay
  useEffect(() => {
    const paymentStatus = searchParams.get('payment_status');
    const paymentType = searchParams.get('type');
    
    if (paymentStatus === 'success' && paymentType === 'onboarding') {
      toast.success('Onboarding payment successful! You can now add clients.', {
        duration: 5000,
      });
      
      // Clear the query params
      setSearchParams({});
      
      // Refresh billing data
      fetchBillingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchBillingData = async () => {
    try {
      const [billingRes, onboardingRes] = await Promise.all([
        axios.get(`${API}/partner/billing`, { headers: getAuthHeader() }),
        axios.get(`${API}/partner/onboarding-status`, { headers: getAuthHeader() })
      ]);
      setBillingData(billingRes.data);
      setOnboardingStatus(onboardingRes.data);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
      toast.error('Failed to load billing data');
    } finally {
      setIsLoading(false);
    }
  };

  const [downloadingInvoice, setDownloadingInvoice] = useState(null);

  const handleDebugInvoice = async (paymentId) => {
    setDebuggingInvoice(paymentId);
    try {
      const response = await axios.get(
        `${API}/invoice/${paymentId}/debug?payment_type=partner_onboarding`,
        { headers: getAuthHeader() }
      );
      setDebugData(response.data);
      setShowDebugModal(true);
    } catch (error) {
      console.error('Debug failed:', error);
      toast.error('Failed to fetch debug info');
    } finally {
      setDebuggingInvoice(null);
    }
  };

  const handleDownloadInvoice = async (invoice) => {
    // Prefer Zoho invoice if available
    const invoiceId = invoice.zoho_invoice_id || invoice.id;
    
    if (!invoice.zoho_invoice_id) {
      toast.error('Invoice not available for download. Please contact support.');
      return;
    }
    
    setDownloadingInvoice(invoice.id);
    try {
      toast.loading('Downloading invoice...', { id: 'download' });
      
      // Download directly from Zoho
      const response = await axios.get(
        `${API}/invoice/${invoice.zoho_invoice_id}/pdf`,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Use invoice number for filename if available
      const filename = invoice.zoho_invoice_number 
        ? `Invoice_${invoice.zoho_invoice_number}.pdf`
        : `Invoice_${invoiceId}.pdf`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded!', { id: 'download' });
    } catch (error) {
      console.error('Failed to download invoice:', error);
      
      let errorMsg = 'Failed to download invoice. Please try again.';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          errorMsg = errorData.detail || errorMsg;
        } catch (e) {
          // Could not parse error
        }
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      
      toast.error(errorMsg, { id: 'download' });
    } finally {
      setDownloadingInvoice(null);
    }
  };

  useEffect(() => {
    fetchBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePayOnboarding = async () => {
    setIsPayingOnboarding(true);
    
    try {
      // Call Checkout SDK initiation endpoint
      const response = await axios.post(
        `${API}/partner/onboarding/checkout-sdk/initiate`,
        {},
        { headers: getAuthHeader() }
      );
      
      const data = response.data;
      
      // Fee is 0 — auto-activated by backend
      if (data.status === 'paid' && data.amount === 0) {
        setIsPayingOnboarding(false);
        toast.success(data.message || 'Onboarding complete! Your account has been activated.');
        fetchBillingData();
        return;
      }
      
      if (data.success && data.order_id) {
        // Open Razorpay Checkout popup
        openRazorpayCheckout(data);
      } else {
        toast.error('Failed to initiate payment. Please try again.');
        setIsPayingOnboarding(false);
      }
    } catch (error) {
      console.error('Failed to initiate payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setIsPayingOnboarding(false);
    }
  };

  const openRazorpayCheckout = (orderData) => {
    // Check if Razorpay is loaded
    if (!window.Razorpay) {
      toast.error('Payment system not loaded. Please refresh the page.');
      setIsPayingOnboarding(false);
      return;
    }

    const options = {
      key: orderData.key_id,
      amount: orderData.amount,
      currency: orderData.currency,
      name: 'WillKart.ai',
      description: 'Partner Onboarding Fee',
      order_id: orderData.order_id,
      prefill: {
        name: orderData.prefill?.name || '',
        email: orderData.prefill?.email || '',
        contact: orderData.prefill?.contact || ''
      },
      notes: orderData.notes || {},
      theme: {
        color: '#f59e0b' // Amber color
      },
      handler: async function(response) {
        // Payment successful - verify on backend
        await verifyOnboardingPayment(response);
      },
      modal: {
        ondismiss: function() {
          setIsPayingOnboarding(false);
          toast.info('Payment cancelled');
        }
      }
    };

    const rzp = new window.Razorpay(options);
    
    rzp.on('payment.failed', function(response) {
      setIsPayingOnboarding(false);
      const errorMsg = response.error?.description || 'Payment failed. Please try again.';
      toast.error(errorMsg);
      console.error('Payment failed:', response.error);
    });

    rzp.open();
  };

  const verifyOnboardingPayment = async (razorpayResponse) => {
    try {
      const response = await axios.post(
        `${API}/partner/onboarding/checkout-sdk/verify`,
        {
          razorpay_order_id: razorpayResponse.razorpay_order_id,
          razorpay_payment_id: razorpayResponse.razorpay_payment_id,
          razorpay_signature: razorpayResponse.razorpay_signature
        },
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        setIsPayingOnboarding(false);
        toast.success('Onboarding payment successful! You can now add clients.');
        
        // Refresh billing data
        fetchBillingData();
      } else {
        throw new Error('Verification failed');
      }
    } catch (error) {
      console.error('Payment verification failed:', error);
      setIsPayingOnboarding(false);
      toast.error('Payment verification failed. Please contact support if amount was deducted.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    // Format in IST (India Standard Time)
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata'  // Force IST timezone
    });
  };

  const getPaymentTypeLabel = (type) => {
    switch (type) {
      case 'partner_onboarding':
        return 'Onboarding Fee';
      default:
        return 'Payment';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="partner-billing">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Billing</h1>
        <p className="text-slate-400 mt-1">View your payments and invoices</p>
      </div>

      {/* Onboarding Payment Banner - Show if not paid */}
      {!onboardingStatus.onboarding_paid && (
        <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-amber-500/20">
                  <AlertTriangle className="w-8 h-8 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">Complete Your Onboarding</h3>
                  <p className="text-slate-300 mb-2">
                    Pay the one-time onboarding fee to unlock all partner features including client management and brokerage modules.
                  </p>
                  <div className="flex items-center gap-4 text-sm text-slate-400">
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-emerald-400" /> Add & manage clients
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-emerald-400" /> Upload brokerage data
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-emerald-400" /> Full dashboard access
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="text-right mb-2">
                  <p className="text-slate-400 text-sm">Onboarding Fee</p>
                  <p className="text-3xl font-bold text-amber-400">
                    ₹{onboardingStatus.onboarding_fee?.toLocaleString('en-IN')}
                  </p>
                  <p className="text-slate-500 text-xs">One-time payment</p>
                </div>
                <Button
                  onClick={handlePayOnboarding}
                  disabled={isPayingOnboarding}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-8 py-6 text-lg shadow-lg shadow-amber-500/25"
                  data-testid="pay-onboarding-btn"
                >
                  {isPayingOnboarding ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay Now
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Onboarding Completed Banner */}
      {onboardingStatus.onboarding_paid && (
        <Card className="bg-emerald-500/10 border-emerald-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
              <div>
                <p className="text-emerald-400 font-medium">Onboarding Complete</p>
                <p className="text-slate-400 text-sm">
                  {onboardingStatus.is_demo 
                    ? 'Demo Account - No payment required'
                    : `Paid on ${formatDate(onboardingStatus.onboarding_paid_at)}`
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 text-sm font-medium">Total Paid</p>
                <p className="text-3xl font-bold text-white mt-2">
                  ₹{billingData.total_paid?.toLocaleString('en-IN') || 0}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-500/10">
                <IndianRupee className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 text-sm font-medium">Total Invoices</p>
                <p className="text-3xl font-bold text-white mt-2">
                  {billingData.invoices?.length || 0}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-blue-500/10">
                <Receipt className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoices List */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <CardTitle className="text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-amber-400" />
            Invoices
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {!billingData.invoices || billingData.invoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Receipt className="w-12 h-12 text-slate-600 mb-4" />
              <p className="text-slate-400 text-lg">No invoices yet</p>
              <p className="text-slate-500 text-sm mt-1">Your invoices will appear here after payments</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {billingData.invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="p-4 hover:bg-slate-800/30 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-amber-500/10">
                        <Receipt className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          ₹{invoice.amount?.toLocaleString('en-IN')}
                        </p>
                        <div className="flex items-center gap-3 text-sm text-slate-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(invoice.paid_at)}
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                            {getPaymentTypeLabel(invoice.payment_type)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-slate-600 text-slate-300 hover:bg-slate-800"
                        onClick={() => handleDebugInvoice(invoice.id)}
                        disabled={debuggingInvoice === invoice.id}
                        data-testid={`debug-invoice-${invoice.id}`}
                      >
                        {debuggingInvoice === invoice.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Bug className="w-4 h-4" />
                        )}
                      </Button>
                      {invoice.zoho_invoice_id ? (
                        <Button
                          size="sm"
                          className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                          onClick={() => handleDownloadInvoice(invoice)}
                          disabled={downloadingInvoice === invoice.id}
                          data-testid={`download-invoice-${invoice.id}`}
                        >
                          {downloadingInvoice === invoice.id ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Downloading...
                            </>
                          ) : (
                            <>
                              <Download className="w-4 h-4 mr-2" />
                              Download
                            </>
                          )}
                        </Button>
                      ) : (
                        <span className="text-slate-500 text-sm">Invoice not available</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-6">
          <h3 className="text-white font-medium mb-2">Need Help?</h3>
          <p className="text-slate-400 text-sm">
            For billing inquiries or payment issues, please contact us at{' '}
            <a href="mailto:help@willkart.ai" className="text-amber-400 hover:underline">
              help@willkart.ai
            </a>
          </p>
        </CardContent>
      </Card>

      {/* Debug Modal */}
      {showDebugModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Bug className="w-5 h-5 text-amber-400" />
                Invoice Debug Info
              </h3>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowDebugModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(80vh-80px)]">
              {debugData?.steps?.map((step, index) => (
                <div key={index} className="mb-4 p-3 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                      step.status === 'SUCCESS' ? 'bg-emerald-500/20 text-emerald-400' :
                      step.status === 'FAILED' ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {step.status}
                    </span>
                    <span className="text-white font-medium">{step.step}</span>
                  </div>
                  <pre className="text-xs text-slate-300 bg-slate-900 p-2 rounded overflow-x-auto">
                    {JSON.stringify(step, null, 2)}
                  </pre>
                </div>
              ))}
              {debugData?.error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 font-medium">Error:</p>
                  <pre className="text-xs text-red-300 mt-1">{debugData.error}</pre>
                </div>
              )}
              <div className="mt-4 p-3 bg-slate-800/50 rounded-lg">
                <p className="text-slate-400 text-xs mb-2">Full Response:</p>
                <pre className="text-xs text-slate-300 bg-slate-900 p-2 rounded overflow-x-auto">
                  {JSON.stringify(debugData, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerBilling;
